'use client';

import { ReactNode, Suspense, useEffect, useMemo, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import Header from './Header';
import SideNav from './SideNav';
import SupabasePreconnect from './SupabasePreconnect';
import Breadcrumbs from './Breadcrumbs';
import { Sidebar, MainContent } from '@/components/ui';
import { ToastProvider } from '@/components/ui/toast';
import { ConfirmProvider } from '@/components/ui/ConfirmDialog';
import { DirtyGuardProvider } from '@/components/ui/dirty-guard';
import { usePostHogUser } from '@/hooks/usePostHogUser';
import { useSideNavCollapse } from '@/hooks/useSideNavCollapse';
import { BreadcrumbProvider, useBreadcrumb } from '@/contexts/BreadcrumbContext';
import { applyTheme, getStoredTheme } from '@/lib/theme';
import { isFederationPortalPath } from './federationPortalPath';

/** Forces light mode while mounted — blocks system theme changes and ThemeProvider updates */
function ForceLightMode() {
  useEffect(() => {
    applyTheme('light');

    // MutationObserver to immediately revert if anything adds the 'dark' class
    const observer = new MutationObserver(() => {
      if (document.documentElement.classList.contains('dark')) {
        document.documentElement.classList.remove('dark');
      }
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      observer.disconnect();
      applyTheme(getStoredTheme());
    };
  }, []);
  return null;
}

type AppShellProps = {
  locale: string;
  children: ReactNode;
};

function BreadcrumbsWithContext() {
  const { customBreadcrumbs, hasCustomBreadcrumbs } = useBreadcrumb();
  return (
    <div data-no-print>
      <Breadcrumbs
        customBreadcrumbs={customBreadcrumbs || undefined}
        hasCustomBreadcrumbs={hasCustomBreadcrumbs}
      />
    </div>
  );
}

function AppShellInner({ locale, children }: { locale: string; children: ReactNode }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { collapsed, toggleCollapsed } = useSideNavCollapse();

  usePostHogUser();

  const isAuthRoute = useMemo(() => {
    if (!pathname) return false;
    const normalized = pathname.replace(/\/$/, '');
    // Auth pages
    const isAuth =
      normalized === `/${locale}` ||
      normalized === `/${locale}/login` ||
      normalized.startsWith(`/${locale}/login/`) ||
      normalized === `/${locale}/register` ||
      normalized.startsWith(`/${locale}/register/`) ||
      normalized === `/${locale}/register/success` ||
      normalized.startsWith(`/${locale}/register/success/`) ||
      normalized === `/${locale}/forgot-password` ||
      normalized.startsWith(`/${locale}/forgot-password/`) ||
      normalized === `/${locale}/reset-password` ||
      normalized.startsWith(`/${locale}/reset-password/`);

    // Full-screen app experiences (no nav shell)
    const isFullScreen =
      normalized.startsWith(`/${locale}/match/score/`) || // scoring screen: /match/score/[matchId]
      normalized === `/${locale}/match/score` ||
      normalized.includes('/live-schedule') || // event live-schedule (public + admin)
      normalized.includes('/celebration') || // event celebration: /event/[id]/[slug]/celebration
      (normalized.includes('/checkin') && searchParams.get('mode') === 'kiosk'); // kiosk check-in

    // Self-chromed modules: the club portal ships its own nav shell
    // (src/app/[locale]/club/layout.tsx renders ClubShell) — no app sidebar.
    const isClubPortal =
      normalized === `/${locale}/club` || normalized.startsWith(`/${locale}/club/`);
    // The federation portal is self-chromed too (FederationShell); its public
    // `/federation/<slug>/register` pages are not.
    const isFederationPortal = isFederationPortalPath(normalized, locale);

    // Public marketing pages
    const isPublic =
      normalized === `/${locale}/blogs` ||
      normalized.startsWith(`/${locale}/blogs/`) ||
      normalized === `/${locale}/about` ||
      normalized.startsWith(`/${locale}/federation/`) ||
      normalized.startsWith(`/${locale}/series/`) ||
      normalized === `/${locale}/sports` ||
      normalized.endsWith('-tournament-software');

    return isAuth || isFullScreen || isClubPortal || isFederationPortal || isPublic;
  }, [pathname, locale, searchParams]);

  // Force light mode on public marketing pages (home, federation, sport, about, blogs)
  const isPublicMarketing = useMemo(() => {
    if (!pathname) return false;
    const normalized = pathname.replace(/\/$/, '');
    return (
      normalized === `/${locale}` ||
      normalized === `/${locale}/about` ||
      normalized === `/${locale}/blogs` ||
      normalized.startsWith(`/${locale}/blogs/`) ||
      (normalized.startsWith(`/${locale}/federation/`) &&
        !isFederationPortalPath(normalized, locale)) ||
      normalized === `/${locale}/sports` ||
      normalized.endsWith('-tournament-software')
    );
  }, [pathname, locale]);

  if (isAuthRoute) {
    return (
      <div className="bg-background min-h-screen">
        {isPublicMarketing ? <ForceLightMode /> : <SupabasePreconnect />}
        {children}
      </div>
    );
  }

  return (
    <div className="bg-background flex h-screen">
      <SupabasePreconnect />
      {sidebarOpen && (
        <div
          className="bg-background/80 fixed inset-0 z-40 backdrop-blur-sm sm:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        className={`fixed inset-y-0 left-0 z-50 transform transition-all duration-300 ease-in-out sm:relative sm:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${collapsed ? 'w-20' : 'w-64'}`}
        data-no-print
      >
        <SideNav
          collapsed={collapsed}
          onClose={() => setSidebarOpen(false)}
          onToggleCollapse={toggleCollapsed}
        />
      </Sidebar>

      <MainContent className="flex-1">
        <div className="sm:hidden" data-no-print>
          <Header onMenuClick={() => setSidebarOpen(true)} />
        </div>

        <div className="pt-2">
          <BreadcrumbsWithContext />
          {children}
        </div>
      </MainContent>
    </div>
  );
}

// i18n context is provided above this component by the locale's client message
// provider (src/i18n/client/*), mounted in `[locale]/layout.tsx`.
export default function AppShell({ locale, children }: AppShellProps) {
  return (
    <ToastProvider>
      <ConfirmProvider>
        <DirtyGuardProvider>
          <BreadcrumbProvider>
            <Suspense fallback={<div className="bg-background min-h-screen">{children}</div>}>
              <AppShellInner locale={locale}>{children}</AppShellInner>
            </Suspense>
          </BreadcrumbProvider>
        </DirtyGuardProvider>
      </ConfirmProvider>
    </ToastProvider>
  );
}
